/**
 * Railway CRUD Client — drop-in replacement for the Supabase client.
 *
 * Provides the same chainable API as @supabase/supabase-js:
 *   client.from("products").select("*").eq("id", id).single()
 *
 * Under the hood, sends a POST to the Railway admin CRUD proxy.
 * All requests require admin JWT authentication.
 */

type FilterOp = "eq" | "neq" | "gt" | "gte" | "lt" | "lte" | "in" | "is" | "ilike" | "like" | "not" | "contains" | "or";

interface QueryFilter {
  column: string;
  op: FilterOp;
  value: any;
}

interface OrderSpec {
  column: string;
  ascending?: boolean;
}

interface QueryResult<T = any> {
  data: T | null;
  error: { message: string; code?: string } | null;
  count?: number;
}

class QueryBuilder<T = any> {
  private _table: string;
  private _operation: "select" | "insert" | "update" | "delete" | "upsert" = "select";
  private _select: string = "*";
  private _filters: QueryFilter[] = [];
  private _order: OrderSpec[] = [];
  private _limit?: number;
  private _offset?: number;
  private _single: boolean = false;
  private _data?: any;
  private _count?: "exact" | "planned" | "estimated";
  private _onConflict?: string;
  private _apiBase: string;
  private _getToken: () => string | null;

  constructor(table: string, apiBase: string, getToken: () => string | null) {
    this._table = table;
    this._apiBase = apiBase;
    this._getToken = getToken;
  }

  select(columns: string = "*", opts?: { count?: "exact" | "planned" | "estimated" }): this {
    // Only set operation to "select" if no other operation was set yet.
    // This allows .insert(data).select("id") to work — select() after insert/update/upsert
    // sets the RETURNING clause, not the operation.
    if (!this._data && this._operation === "select") {
      this._operation = "select";
    }
    this._select = columns;
    if (opts?.count) this._count = opts.count;
    return this;
  }

  insert(data: any): this {
    this._operation = "insert";
    this._data = data;
    return this;
  }

  update(data: any): this {
    this._operation = "update";
    this._data = data;
    return this;
  }

  delete(): this {
    this._operation = "delete";
    return this;
  }

  upsert(data: any, opts?: { onConflict?: string }): this {
    this._operation = "upsert";
    this._data = data;
    if (opts?.onConflict) this._onConflict = opts.onConflict;
    return this;
  }

  eq(column: string, value: any): this { this._filters.push({ column, op: "eq", value }); return this; }
  neq(column: string, value: any): this { this._filters.push({ column, op: "neq", value }); return this; }
  gt(column: string, value: any): this { this._filters.push({ column, op: "gt", value }); return this; }
  gte(column: string, value: any): this { this._filters.push({ column, op: "gte", value }); return this; }
  lt(column: string, value: any): this { this._filters.push({ column, op: "lt", value }); return this; }
  lte(column: string, value: any): this { this._filters.push({ column, op: "lte", value }); return this; }
  in(column: string, values: any[]): this { this._filters.push({ column, op: "in", value: values }); return this; }
  is(column: string, value: null): this { this._filters.push({ column, op: "is", value }); return this; }
  ilike(column: string, pattern: string): this { this._filters.push({ column, op: "ilike", value: pattern }); return this; }
  like(column: string, pattern: string): this { this._filters.push({ column, op: "like", value: pattern }); return this; }
  not(column: string, innerOp: string, value: any): this { this._filters.push({ column, op: "not", value: { op: innerOp, value } }); return this; }
  contains(column: string, value: any): this { this._filters.push({ column, op: "contains", value }); return this; }
  or(expression: string): this { this._filters.push({ column: "", op: "or", value: expression }); return this; }

  order(column: string, opts?: { ascending?: boolean }): this {
    this._order.push({ column, ascending: opts?.ascending ?? true });
    return this;
  }

  limit(n: number): this { this._limit = n; return this; }

  range(from: number, to: number): this {
    this._offset = from;
    this._limit = to - from + 1;
    return this;
  }

  single(): Promise<QueryResult<T>> { this._single = true; return this.execute(); }
  maybeSingle(): Promise<QueryResult<T | null>> { this._single = true; return this.execute(); }

  then<TResult1 = QueryResult<T[]>, TResult2 = never>(
    onfulfilled?: ((value: QueryResult<T[]>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
  ): Promise<TResult1 | TResult2> {
    return this.execute().then(onfulfilled as any, onrejected);
  }

  private async execute(): Promise<QueryResult<any>> {
    const token = this._getToken();

    const body: any = {
      table: this._table,
      operation: this._operation,
      select: this._select,
      filters: this._filters.length > 0 ? this._filters : undefined,
      data: this._data,
      order: this._order.length > 0 ? this._order : undefined,
      limit: this._limit,
      offset: this._offset,
      single: this._single || undefined,
      count: this._count,
      onConflict: this._onConflict,
    };

    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${this._apiBase}/api/v1/admin/query`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      const json = await res.json();

      if (!res.ok) {
        return {
          data: null,
          error: { message: json.error?.message || `HTTP ${res.status}`, code: json.error?.code },
          ...(json.count !== undefined ? { count: json.count } : {}),
        };
      }

      return {
        data: json.data,
        error: json.error || null,
        ...(json.count !== undefined ? { count: json.count } : {}),
      };
    } catch (err: any) {
      return { data: null, error: { message: err.message } };
    }
  }
}

/**
 * Railway-backed Supabase-compatible client.
 * Use as a drop-in replacement for the Supabase client in admin.
 */
export class RailwayClient {
  private apiBase: string;
  private _getToken: () => string | null;

  auth: RailwayAuth;

  constructor(apiBase: string, getToken: () => string | null) {
    this.apiBase = apiBase;
    this._getToken = getToken;
    this.auth = new RailwayAuth(apiBase, getToken);
  }

  from<T = any>(table: string): QueryBuilder<T> {
    return new QueryBuilder<T>(table, this.apiBase, this._getToken);
  }

  // Stub for channel() — returns a no-op object for realtime subscriptions
  channel(name: string): any {
    return {
      on: () => ({ subscribe: () => ({ unsubscribe: () => {} }) }),
      subscribe: () => ({ unsubscribe: () => {} }),
    };
  }

  removeChannel(channel: any): void {
    // No-op — realtime not supported
  }
}

class RailwayAuth {
  private apiBase: string;
  private _getToken: () => string | null;
  private _listeners: Set<(event: string, session: any) => void> = new Set();
  private _session: any = null;

  constructor(apiBase: string, getToken: () => string | null) {
    this.apiBase = apiBase;
    this._getToken = getToken;
  }

  async signInWithPassword(credentials: { email: string; password: string }): Promise<any> {
    const res = await fetch(`${this.apiBase}/store/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(credentials),
    });
    const json = await res.json();

    if (!res.ok || !json.data?.session) {
      return { data: { user: null, session: null }, error: json.error || { message: "Login failed" } };
    }

    this._session = json.data.session;
    const user = json.data.user;

    // Notify listeners
    for (const listener of this._listeners) {
      listener("SIGNED_IN", { user, access_token: json.data.session.access_token });
    }

    return {
      data: {
        user: { id: user.id, email: user.email },
        session: json.data.session,
      },
      error: null,
    };
  }

  async signUp(credentials: { email: string; password: string; options?: any }): Promise<any> {
    const res = await fetch(`${this.apiBase}/store/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password,
        ...credentials.options?.data,
      }),
    });
    const json = await res.json();
    if (!res.ok) return { data: { user: null }, error: json.error };
    return { data: { user: json.data.user }, error: null };
  }

  async signOut(): Promise<any> {
    this._session = null;
    for (const listener of this._listeners) {
      listener("SIGNED_OUT", null);
    }
    return { error: null };
  }

  async getSession(): Promise<any> {
    const token = this._getToken();
    if (!token) return { data: { session: null }, error: null };
    return { data: { session: { access_token: token } }, error: null };
  }

  async getUser(): Promise<any> {
    const token = this._getToken();
    if (!token) return { data: { user: null }, error: null };

    const res = await fetch(`${this.apiBase}/store/account/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    if (!res.ok) return { data: { user: null }, error: json.error };
    return { data: { user: json.data }, error: null };
  }

  onAuthStateChange(callback: (event: string, session: any) => void): { data: { subscription: { unsubscribe: () => void } } } {
    this._listeners.add(callback);

    // Check for existing session
    const token = this._getToken();
    if (token) {
      setTimeout(() => callback("INITIAL_SESSION", { access_token: token }), 0);
    }

    return {
      data: {
        subscription: {
          unsubscribe: () => this._listeners.delete(callback),
        },
      },
    };
  }
}

/**
 * Create a Railway-backed client.
 * API_BASE should be the Railway API URL (e.g., https://api.miluliem.lv)
 * getToken should return the current JWT access token from storage.
 */
export function createRailwayClient(apiBase: string, getToken: () => string | null): RailwayClient {
  return new RailwayClient(apiBase, getToken);
}
